
<app-call-pathway-roleplay *ngIf="callFlow() === 'role-play'"
  [controlService]="controlService"
  [realtimeService]="realtimeService"
  [userVideoService]="userVideoService"
  [deviceSettingsService]="deviceSettingsService"
  [playerService]="playerService"
  [callStore]="callStore"
  [enrollmentStepUuid]="enrollmentStepUuid"
  [enrollmentUuid]="enrollmentUuid"
  [stepType]="stepType"
  [callUuid]="rolePlayCallUuid()"
  [callType]="rolePlayCallType()"
  [runStepResponse]="runStepResponse"
  [endedEarly]="roleplayEndedEarly()"
  (settingsClicked)="handleSettingsClick()"
  (leaveStepClicked)="handleLeaveStepClick()"
  (returnToPathway)="handleReturnToPathway()"
  (confirmActionClicked)="handleRoleplayConfirmAction($event)"
  (retryRoleplayClicked)="handleRetryRoleplay()"
  [title]="title()"
></app-call-pathway-roleplay>

<app-device-settings-call
  *ngIf="showDeviceSettingsScreen()"
  [deviceSettingsService]="deviceSettingsService"
  [controlService]="controlService"
  [playerService]="playerService"
></app-device-settings-call>

<ng-container *ngIf="callFlow() !== 'role-play'">

  <!-- Call Header -->
  <app-pathway-call-header
    *ngIf="!showDeviceSettingsScreen()"
    [isLoading]="isLoading()"
    [title]="title()"
    [showHelpIcon]="isLearnStep"
    [showDownloadIcon]="isPdfDownloadEnabled()"
    (helpIconClicked)="handleHelpIconClick()"
    (settingsClicked)="handleSettingsClick()"
    (leaveStepClicked)="handleLeaveStepClick()"
    (downloadClicked)="handleDownloadPdf()"
  >
  </app-pathway-call-header>

  @if (callStore?.getIsReconnectingAudio()) {
    <div class="reconnecting-banner">{{ 'call.RECONNECTING_AUDIO' | transloco }}</div>
  }

  <!-- Call Main Content -->
  <app-pathway-call-main
    *ngIf="!showDeviceSettingsScreen()"
    [isLoading]="mainIsLoading()"
    [isPdf]="callFlow() === 'pdf'"
  >
    @switch (callFlow()) { @case ('guide') {
    <app-call-pathway-guide
      [controlService]="controlService"
      [realtimeService]="realtimeService"
      [callStore]="callStore"
      [playerService]="playerService"
      [deviceSettingsService]="deviceSettingsService"
      [hideUserVideo]="isPreamble() && stepType !== 'QUIZ'"
      [isVisualAidCall]="controlService?.isVisualAidCall ?? false"
    />
    } @case ('pdf') {
    <app-call-pathway-pdf
      [pdfUrl]="pdfViewerUrl()"
      [enrollmentUuid]="enrollmentUuid"
      [initialPage]="initialPdfPage()"
      [isFirstAttempt]="isFirstAttempt()"
      (progressChange)="onPdfProgressChange($event)"
      (pdfCompleted)="handlePdfCompleted()"
      [callLearnUuid]="callLearnUuid()"
    />
    } @case ('video') {
    <app-call-pathway-video
      [src]="videoSrc()"
      [firstAttempt]="isFirstAttempt()"
      [startTime]="videoStartTimeMs() / 1000"
      [enrollmentUuid]="enrollmentUuid"
      [callLearnUuid]="callLearnUuid()"
      (videoReady)="onVideoReady($event)"
      (videoPlay)="onVideoPlay()"
      (videoEnded)="handleVideoEnded()"
      (videoTimeUpdate)="onVideoTimeUpdate($event)"
      (videoPause)="onVideoPause()"
    />}
    @case ('scorm') {
    <app-call-pathway-scorm
      [scormUrl]="scormUrl()"
      [enrollmentUuid]="enrollmentUuid"
      [instanceUuid]="instanceUuid"
      [callUuid]="scormCallUuid()"
      [practiceCallUuid]="scormPracticeCallUuid()"
      [initialState]="scormInitialState()"
      [packageContentUuid]="scormPackageContentUuid()"
      [children]="scormChildren()"
      [firstAttempt]="isFirstAttempt()"
      (scormCompleted)="handleScormCompleted()"
    />}
    @case ('error') {
    <app-call-pathway-error />
    } @case ('quiz-result') {
    <app-quiz-result
      [callStore]="callStore"
      [outcome]="quizOutcome()"
      [passed]="quizPassed()"
      [ttsReady]="quizResultTtsReady()"
      [stepsToReview]="quizStepsToReview()"
      [isVisualAidCall]="controlService?.isVisualAidCall ?? false"
      (subtitleTextChange)="onQuizResultSubtitle($event)"
      (ttsPlayingChange)="onQuizResultTtsPlaying($event)"
      (avatarThumbnailReady)="onQuizResultAvatarThumbnail($event)"
    />
    } }
  </app-pathway-call-main>

  <!-- Call Footer -->
  <app-pathway-call-footer
    *ngIf="!showDeviceSettingsScreen() && stepType && !isRoleplayStepType && !mainIsLoading()"
    [captionText]="captionText()"
    [subtitleKind]="subtitle().kind"
    [subtitleText]="subtitle().text"
    [subtitleFormatting]="subtitle().formatting"
    [subtitleIcon]="subtitle().icon"
    [avatarPhotoUrl]="avatarPhotoUrl()"
    [showAvatarPhoto]="showAvatarPhoto()"
    [showSubtitles]="callFlow() !== 'quiz-result' || !quizResultTtsDone()"
  >
    <app-pathway-call-controls
      [isMicOn]="callStore?.getIsMicOn() ?? false"
      [disableMicButton]="disableMicButton"
      (microphoneClicked)="handleMicrophoneClick()"
      [isVideoOn]="controlService?.isVideoOn ?? false"
      [isCameraAvailable]="getCameraAvailable()"
      [disableVideoButton]="disableVideoButton"
      [disableConfirmActionButton]="
        !isPreamble() && (disableConfirmActionButton() || mainIsLoading())
      "
      [suppressConfirmAction]="
        isPreamble() && (disableConfirmActionButton() || mainIsLoading())
      "
      [confirmActionLabel]="getConfirmActionLabel()"
      (videoClicked)="handleVideoClick()"
      (onConfirmActionClicked)="handleOnConfirmActionClick()"
      [showMicrophoneButton]="shouldShowMicControl()"
      [showCameraButton]="shouldShowVideoControl()"
    />
  </app-pathway-call-footer>

  <!-- Leave step early modal -->
  <app-call-leave-early
    [open]="leaveStepModalOpen()"
    [controlService]="controlService"
    [realtimeService]="realtimeService"
    [description]="leaveStepModalDescription()"
    (confirmLeave)="handleLeaveStepConfirm()"
    (resume)="handleLeaveStepResume()"
  ></app-call-leave-early>

  <!-- Learn step help modal — only mounted during pdf/video learn steps -->
  @if (learnStepType) {
    <app-learn-step-help-modal
      [open]="learnStepHelpModalOpen()"
      [title]="title()"
      [stepType]="learnStepType"
      (closed)="learnStepHelpModalOpen.set(false)"
    />
  }

</ng-container>

<!-- Settings modal -->
<app-device-settings-modal
  *ngIf="deviceSettingsService?.displayModal"
  [deviceSettingsService]="deviceSettingsService"
  [controlService]="controlService"
></app-device-settings-modal>

<!-- No response modal -->
<app-no-response-detected
  *ngIf="callStore?.getOpenNoResponseModal()"
  [controlService]="controlService"
  [playerService]="playerService"
  [deviceSettingsService]="deviceSettingsService"
  [realtimeService]="realtimeService"
></app-no-response-detected>
